// Central extension registry for the TipTap document editor.
// Combines built-in TipTap extensions with our custom block nodes.

import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import { Table } from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import Image from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import type { Extensions } from '@tiptap/react';
import { Extension } from '@tiptap/core';
import { Plugin } from '@tiptap/pm/state';

import { CalloutNode, ToggleNode, ChartNode, DiagramNode, EquationNode } from './nodes';
import { SlashCommand } from './SlashCommand';

// Keep the server block identity on the editable node. Formatting commands
// can wrap or split nodes; array indexes are not stable across those changes.
export function createPersistentBlockIdPlugin() {
  return new Plugin({
      appendTransaction: (_transactions, _oldState, newState) => {
        const seen = new Set<string>();
        const tr = newState.tr;
        newState.doc.descendants((node, pos, parent) => {
          // A list item is one server block. Its child paragraph is only its
          // editable content and must not claim a second identity.
          const isListItem = node.type.name === 'listItem' || node.type.name === 'taskItem';
          const wrapsParagraph = isListItem || node.type.name === 'blockquote';
          const isTopLevel = parent === newState.doc;
          if (!isListItem && !isTopLevel) return;
          if (['bulletList', 'orderedList', 'taskList'].includes(node.type.name)) return;
          const inherited = wrapsParagraph ? node.firstChild?.attrs.blockId : undefined;
          let blockId: string | undefined = node.attrs.blockId || inherited;
          if (!blockId || seen.has(blockId)) blockId = crypto.randomUUID();
          seen.add(blockId);
          if (node.attrs.blockId !== blockId) {
            tr.setNodeMarkup(pos, undefined, { ...node.attrs, blockId });
          }
          if (wrapsParagraph && node.firstChild?.type.name === 'paragraph' && node.firstChild.attrs.blockId !== blockId) {
            tr.setNodeMarkup(pos + 1, undefined, { ...node.firstChild.attrs, blockId });
          }
        });
        return tr.docChanged ? tr : null;
      },
    });
}

const PersistentBlockId = Extension.create({
  name: 'persistentBlockId',
  addGlobalAttributes() {
    return [{
      types: ['paragraph', 'heading', 'listItem', 'taskItem', 'blockquote', 'codeBlock', 'horizontalRule', 'image', 'bulletList', 'orderedList', 'taskList', 'callout', 'toggle'],
      attributes: {
        blockId: {
          default: null,
          parseHTML: (element) => element.getAttribute('data-block-id'),
          renderHTML: (attributes) => attributes.blockId ? { 'data-block-id': attributes.blockId } : {},
        },
      },
    }];
  },
  addProseMirrorPlugins() {
    return [createPersistentBlockIdPlugin()];
  },
});

export function createExtensions(placeholder?: string): Extensions {
  return [
    StarterKit.configure({
      heading: { levels: [1, 2, 3] },
      codeBlock: false, // We use CodeBlockLowlight separately or default for now
      // horizontalRule, blockquote, bulletList, orderedList, listItem are enabled by default
    }),
    PersistentBlockId,
    Underline,
    Link.configure({
      openOnClick: false,
      HTMLAttributes: { class: 'text-primary underline cursor-pointer' },
    }),
    Placeholder.configure({
      placeholder: placeholder ?? "Type '/' for commands...",
    }),
    TextStyle,
    Color,
    Highlight.configure({ multicolor: true }),
    TaskList,
    TaskItem.configure({ nested: true }),
    Table.configure({ resizable: true }),
    TableRow,
    TableCell,
    TableHeader,
    Image,
    TextAlign.configure({ types: ['heading', 'paragraph'] }),
    // Custom nodes
    CalloutNode,
    ToggleNode,
    ChartNode,
    DiagramNode,
    EquationNode,
    // Slash command menu
    SlashCommand,
  ];
}
